import ArtistRadioPlayer from 'widget/ArtistRadio/ArtistRadioPlayer';
import Container from 'widget/components/Container';
import ExternalLink from 'widget/components/ExternalLink';
import helpers from 'widget/ArtistRadio/helpers';
import IHeartLink from 'widget/components/IHeartLink';
import MainContent from 'widget/components/MainContent';
import PLAYED_FROM from 'modules/Analytics/constants/playedFrom';
import Player from 'widget/contexts/Player';
import PlayerState, {
  PlayerErrorState,
} from 'modules/Player/constants/playerState';
import ShouldShow from 'components/ShouldShow';
import Tracks from 'widget/ArtistRadio/Tracks';
import useMount from 'hooks/useMount';
import useTrackPageViewOnMount from 'widget/hooks/useTrackPageViewOnMount';
import { ComponentProps } from 'widget/ArtistRadio/types';
import { getErrorMessageTranslator } from 'widget/Errors/PlaybackError/helpers/getErrorMessageTranslator';
import { useContext, useEffect, useRef } from 'react';
import { useSession } from 'widget/utils/session';
import { useTranslate } from 'widget/hooks';

function ArtistRadio({
  ampUrl,
  analytics,
  artistId,
  artistUrl,
  autoplay,
  customRadioEnabled,
  embedUrl,
  followUrl,
  links,
  image,
  name,
  similarArtists,
  similarArtistsUrl,
  stationArtist,
  transport,
  websiteUrl,
}: ComponentProps) {
  const translate = useTranslate();
  const { getCurrentlyPlaying, playerState, skipDisabled } = useContext(
    Player.Context,
  );
  const {
    artistId: currentArtistId = '',
    artistName: currentArtist = '',
    isError,
    songName: currentSong = '',
    trackId = '',
    trackImage: currentTrackImage = '',
  } = getCurrentlyPlaying() || {};
  const { profileId, sessionId } = useSession();
  const { radioEditMediaServer } = links;
  let pageHost = '';
  let pageURL = '';

  if (__CLIENT__ && document.referrer) {
    const { host, href } = new URL(document.referrer);
    pageHost = host;
    pageURL = href;
  }

  const songUrl = useRef('');
  useEffect(() => {
    const [ws, delimiter, qs] = websiteUrl?.split(/(\?)/) ?? ['', '', ''];
    songUrl.current = `${ws}/artist/${currentArtistId}/songs/${trackId}${delimiter}${qs}`;
  }, [trackId]);

  useTrackPageViewOnMount();

  useMount(() => {
    if (analytics) {
      analytics.trackPageView!({
        contentFrame: 'responsive_widget',
        id: `artist|${artistId}`,
        inNetwork: pageHost.includes('iheart').toString(),
        name: stationArtist.name,
        pageHost,
        pageName: 'artist_profile',
        pageURL,
      });
    }
  });

  const displayName = currentArtist || name;

  return (
    <Container>
      <ShouldShow
        shouldShow={
          !Object.values(PlayerErrorState).includes(
            playerState as PlayerErrorState,
          )
        }
      >
        <MainContent
          hasPlayed={[
            PlayerState.Playing,
            PlayerState.Paused,
            PlayerState.Buffering,
          ].includes(playerState as PlayerState)}
          icons={[<IHeartLink href={websiteUrl} key="iheartlink" />]}
          infoHeading="iHeart"
          infoHeadingSmallHeight={`${displayName} • `}
          subHeading={displayName}
          title={
            <ExternalLink
              data-test="title-link"
              href={currentSong ? songUrl.current : artistUrl}
            >
              {currentSong || `${name} Radio`}
            </ExternalLink>
          }
        />
      </ShouldShow>

      <ArtistRadioPlayer
        ampUrl={ampUrl}
        analytics={analytics}
        analyticsData={{
          name,
          playedFrom: PLAYED_FROM.RESP_WIDGET_PLAYER_PLAY,
          subName: currentSong || '',
          subId: trackId,
        }}
        artistRadioUrl={artistUrl}
        autoplay={autoplay}
        customRadioEnabled={customRadioEnabled}
        embedUrl={embedUrl}
        followUrl={followUrl}
        getPlaybackErrorMessage={getErrorMessageTranslator(translate)}
        hasPlayed={false}
        imageUrl={image}
        isError={isError}
        metadata={{
          artist: currentArtist,
          mainImage: image,
          title: currentSong,
          trackImage: currentTrackImage,
        }}
        positionBottom={false}
        profileId={profileId}
        sessionId={sessionId}
        shareText={translate(`Share {name} Radio`, { name })}
        shareTitle={name}
        skipDisabled={skipDisabled}
        stationArtist={stationArtist}
        transport={transport}
        websiteUrl={websiteUrl}
      />

      <Tracks
        analytics={analytics}
        radioEditMediaServer={radioEditMediaServer}
        similarArtists={similarArtists}
        similarArtistsUrl={similarArtistsUrl}
        title={name}
      />
    </Container>
  );
}

ArtistRadio.getInitialProps = helpers.getInitialProps;

export default ArtistRadio;
